import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import BarcodeModal from "../Components/BarcodeScanner";
import Foto from "../Components/Foto";
import { Modal, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import "./auditoria.css";

/* ----------------- Helpers ----------------- */
function calcularDistancia(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLon = (lon2 - lon1) * (Math.PI / 180);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

const parseCurrencyToNumber = (v) => {
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const s = String(v).replace(/\./g, "").replace(/,/g, ".");
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
};

const formatarMoeda = (valor) => {
    if (valor == null || valor === "") return "R$ 0,00";
    const numero = parseCurrencyToNumber(valor);
    return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
    }).format(numero);
};

const formatarData = (data) => {
    if (!data) return "";

    // Se já estiver no formato dd/mm/yyyy
    if (typeof data === "string" && data.includes("/")) return data;

    // Se for formato ISO string (ex: "2026-10-07T00:00:00.000Z")
    if (typeof data === "string" && data.includes("T")) {
        const parteData = data.split("T")[0]; // Separa '2026-10-07'
        const [ano, mes, dia] = parteData.split("-");
        if (ano && mes && dia) return `${dia}/${mes}/${ano}`;
    }

    // Se for formato YYYY-MM-DD
    if (typeof data === "string" && data.includes("-")) {
        const [ano, mes, dia] = data.split("-");
        if (ano && mes && dia) return `${dia}/${mes}/${ano}`;
    }

    // Fallback genérico para instâncias Date
    const d = new Date(data);
    if (isNaN(d.getTime())) return data;
    const dia = String(d.getUTCDate()).padStart(2, "0");
    const mes = String(d.getUTCMonth() + 1).padStart(2, "0");
    const ano = d.getUTCFullYear();
    return `${dia}/${mes}/${ano}`;
};

const flagIcon = (flag) => {
    const active = flag === "S" || flag === "A" || flag === true || flag === "T";
    return active ? (
        <i title="Ativo" className="fas fa-check-circle text-success me-1" />
    ) : (
        <i title="Inativo" className="fas fa-times-circle text-danger me-1" />
    );
};

/* ----------------- Modal Lista de Produtos Encontrados ----------------- */
function ModalListaProdutos({ show, produtos, onSelect, onClose }) {
    if (!show) return null;
    return (
        <Modal show={show} onHide={onClose} centered size="md">
            <Modal.Header closeButton className="py-2">
                <Modal.Title className="fs-6 fw-bold">
                    🔍 Selecione o Produto ({produtos.length})
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-2" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                <div className="list-group">
                    {produtos.map((item, idx) => {
                        const eanItem = item.ean_principal || item.codacesso || item.ean || "";
                        return (
                            <button
                                key={idx}
                                type="button"
                                className="list-group-item list-group-item-action p-2 text-start"
                                onClick={() => onSelect(item)}
                            >
                                <div className="fw-bold text-dark extra-small mb-1">{item.desccompleta}</div>
                                <div className="row g-1 text-muted extra-small">
                                    <div className="col-6">
                                        <span>Cód: <strong>{item.seqproduto}</strong></span>
                                    </div>
                                    <div className="col-6 text-end">
                                        <span>Preço: <strong className="text-primary">{formatarMoeda(item.precovalidnormal || item.precobasenormal)}</strong></span>
                                    </div>
                                    <div className="col-12 mt-1">
                                        <span>EAN: <strong>{eanItem || "N/A"}</strong></span>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </Modal.Body>
        </Modal>
    );
}

/* ----------------- Modal de Estoque por Loja (com Preços, Vigências e Movimentações) ----------------- */
function EstoqueListaCards({ dados }) {
    return (
        <div className="d-flex flex-column gap-2">
            {dados.map((item, idx) => {
                const precoNormal = parseCurrencyToNumber(item.preconormal || item.precovalidnormal);
                const promoVarejo = parseCurrencyToNumber(item.promovarejo);
                const promoApp = parseCurrencyToNumber(item.promoapp);

                return (
                    <div key={idx} className="border rounded-3 p-2 bg-light">
                        {/* Linha Cabeçalho */}
                        <div className="d-flex justify-content-between align-items-center mb-1 pb-1 border-bottom">
                            <span className="fw-bold text-primary extra-small">
                                Loja {String(item.loja).padStart(2, "0")}
                            </span>
                            <div className="d-flex gap-1">
                                <span className={`badge bg-${item.statuscompra === "A" ? "success" : "danger"} extra-small`}>
                                    {flagIcon(item.statuscompra)} Compra
                                </span>
                                <span className={`badge bg-${item.statusvenda === "A" ? "success" : "danger"} extra-small`}>
                                    {flagIcon(item.statusvenda)} Venda
                                </span>
                            </div>
                        </div>

                        {/* Linha de Quantidades */}
                        <div className="row g-1 text-center extra-small mb-2">
                            <div className="col-4">
                                <span className="text-muted d-block">Estoque</span>
                                {(() => {
                                    const stq = parseCurrencyToNumber(item.estoque);
                                    const min = parseCurrencyToNumber(item.min);
                                    const max = parseCurrencyToNumber(item.max);

                                    const isMaiorMax = max > 0 && stq > max;
                                    const isMenorMin = min > 0 && stq < min;

                                    let corTexto = "text-dark";
                                    if (isMaiorMax) corTexto = "text-danger";
                                    if (isMenorMin) corTexto = "text-danger fw-bold";

                                    return (
                                        <span className={`fw-bold ${corTexto} d-inline-flex align-items-center justify-content-center gap-1`}>
                                            {isMenorMin && (
                                                <span className="sirene-animada" title="Estoque abaixo do mínimo!">
                                                    🚨
                                                </span>
                                            )}
                                            {isMaiorMax && (
                                                <span title="Estoque acima do máximo!">
                                                    ⚠️
                                                </span>
                                            )}
                                            {item.estoque ?? 0} {(item.embalagem || "").substring(0, 2)}
                                        </span>
                                    );
                                })()}
                            </div>
                            <div className="col-4">
                                <span className="text-muted d-block">Mínimo</span>
                                <span className="fw-bold text-secondary">{item.min ?? 0}</span>
                            </div>
                            <div className="col-4">
                                <span className="text-muted d-block">Máximo</span>
                                <span className="fw-bold text-secondary">{item.max ?? 0}</span>
                            </div>
                        </div>

                        {/* Linha de Última Entrada e Última Saída */}
                        <div className="row g-1 text-center extra-small mb-2 pt-1 border-top">
                            <div className="col-4">
                                <span className="text-muted d-block">Últ. Ent.</span>
                                <span className="fw-bold text-dark">{formatarData(item.ultima_entrada) || "-"}</span>
                            </div>
                            <div className="col-4">
                                <span className="text-muted d-block">Últ. Saí.</span>
                                <span className="fw-bold text-dark">{formatarData(item.ultima_saida) || "-"}</span>
                            </div>
                            <div className="col-4">
                                <span className="text-muted d-block">Últ. Mov.</span>
                                <span className="fw-bold text-dark">{formatarData(item.ultima_movimentacao) || "-"}</span>
                            </div>
                        </div>

                        {/* Bloco de Preços de Venda e Vigências */}
                        <div className="pt-1 border-top bg-white p-2 rounded">
                            <div className="d-flex justify-content-between align-items-center extra-small">
                                <span className="text-muted">Preço Normal:</span>
                                <span className="fw-bold text-dark">{formatarMoeda(precoNormal)}</span>
                            </div>

                            {/* Promoção Varejo */}
                            {promoVarejo > 0 && (
                                <div className="mt-1 pt-1 border-top border-danger-subtle">
                                    <div className="d-flex justify-content-between align-items-center extra-small text-danger fw-bold">
                                        <span>🏷️ Promoção:</span>
                                        <span>{formatarMoeda(promoVarejo)}</span>
                                    </div>
                                    {(item.iniciovarejo || item.fimvarejo) && (
                                        <div className="d-flex justify-content-between extra-small text-muted opacity-75 mt-1">
                                            <span>Vigência:</span>
                                            <span>{formatarData(item.iniciovarejo)} até {formatarData(item.fimvarejo)}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Promoção App */}
                            {promoApp > 0 && (
                                <div className="mt-1 pt-1 border-top border-success-subtle">
                                    <div className="d-flex justify-content-between align-items-center extra-small text-success fw-bold">
                                        <span>📱 Promoção App:</span>
                                        <span>{formatarMoeda(promoApp)}</span>
                                    </div>
                                    {(item.inicioapp || item.fimapp) && (
                                        <div className="d-flex justify-content-between extra-small text-muted opacity-75 mt-1">
                                            <span>Vigência App:</span>
                                            <span>{formatarData(item.inicioapp)} até {formatarData(item.fimapp)}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ----------------- Loading Modal ----------------- */
function LoadingModal({ show, message }) {
    if (!show) return null;
    return (
        <div className="loading-modal-overlay">
            <div className="loading-modal-content">
                <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-2 small fw-semibold">{message}</div>
            </div>
        </div>
    );
}
// Helper para extrair ID e Nome da Loja independente das chaves retornadas pelo backend/localStorage

const extrairInfoLoja = (item) => {
    if (!item && item !== 0) return { cod: 0, nome: "Loja 00" };

    // Se o item for apenas um número/string (ex: 1, 2, "5")
    if (typeof item === "number" || typeof item === "string") {
        const codNum = parseInt(item, 10) || 0;
        return { cod: codNum, nome: `Loja ${String(codNum).padStart(2, "0")}` };
    }

    // Se for um objeto com variações de propriedades
    const cod =
        item.cod_emp ??
        item.COD_EMP ??
        item.nroempresa ??
        item.NROEMPRESA ??
        item.loja ??
        item.LOJA ??
        item.nro_empresa ??
        0;

    const nome =
        item.razao ??
        item.RAZAO ??
        item.nomereduzido ??
        item.NOMEREDUZIDO ??
        item.nome ??
        item.NOME ??
        item.fantasia ??
        `Loja ${String(cod).padStart(2, "0")}`;

    return { cod: parseInt(cod, 10), nome };
};

/* ----------------- Componente Principal ----------------- */
export default function AuditoriaProdutoV2() {
    const [pesquisa, setPesquisa] = useState("");
    const [produto, setProduto] = useState(null);
    const [listaProdutosBusca, setListaProdutosBusca] = useState([]);
    const [showListaModal, setShowListaModal] = useState(false);

    const [showScanner, setShowScanner] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [dadosEstoque, setDadosEstoque] = useState([]);
    const [showEstoqueModal, setShowEstoqueModal] = useState(false);
    const [isProcessingScan, setIsProcessingScan] = useState(false);

    // Estado para o componente Foto
    const [fotoSelecionada, setFotoSelecionada] = useState(null);

    const [lojaSelecionada, setLojaSelecionada] = useState(null);
    const [dentroDoRaio, setDentroDoRaio] = useState(false);
    const [lojasPermitidasList, setLojasPermitidasList] = useState([]);
    const [usarSelectLojas, setUsarSelectLojas] = useState(false);
    const [ean, setEan] = useState("");

    const navigate = useNavigate();
    const chapa = localStorage.getItem("chapa");
    const usuario = chapa;

    /* ----------------- Áudio do Bip ----------------- */
    const audioRef = useRef(new Audio("/beep.mp3"));

    const playBeep = useCallback(() => {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch((e) => console.log("Erro ao tocar beep:", e));
    }, []);

    const desbloquearAudio = () => {
        if (audioRef.current) {
            audioRef.current
                .play()
                .then(() => {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                })
                .catch(() => { });
        }
    };

    /* ----------------- Localização e Seleção de Loja ----------------- */
    useEffect(() => {
        const permiteVendas = localStorage.getItem("permiteVendas");
        if (!chapa) {
            navigate("/arbor/login");
            return;
        } else if (permiteVendas !== "S") {
            Swal.fire({
                icon: "error",
                title: "Acesso Negado",
                text: "Não é permitido acessar o módulo de auditoria.",
                confirmButtonText: "OK",
            }).then(() => navigate("/arbor/home"));
            return;
        }

        let lojasPermitidas = [];
        try {
            lojasPermitidas = JSON.parse(localStorage.getItem("lojasPermitidas")) || [];
        } catch (e) {
            lojasPermitidas = [];
        }

        const aplicarFallbackLoja = async (lojasServidor) => {
            const lojaUsuario = parseInt(localStorage.getItem("loja") || "0", 10);

            // Se temos lojas do servidor, atualiza o estado para preencher o select corretamente
            const listaParaUso = lojasServidor.length > 0 ? lojasServidor : lojasPermitidas;
            setLojasPermitidasList(listaParaUso);

            if (lojaUsuario > 50 || listaParaUso.length > 0) {
                setUsarSelectLojas(true);

                // Tenta encontrar a loja padrão do usuário dentro da lista
                const lojaPadrao = listaParaUso.find((item) => extrairInfoLoja(item).cod === lojaUsuario) || listaParaUso[0];

                if (lojaPadrao) {
                    const { cod, nome } = extrairInfoLoja(lojaPadrao);
                    setLojaSelecionada({ cod_emp: cod, razao: `${String(cod).padStart(2, "0")} - ${nome}` });
                    setDentroDoRaio(true);
                } else {
                    setDentroDoRaio(false);
                }
                return;
            }

            const lojaEncontrada = lojasServidor.find((l) => extrairInfoLoja(l).cod === lojaUsuario);

            if (lojaEncontrada) {
                const { cod, nome } = extrairInfoLoja(lojaEncontrada);
                setLojaSelecionada({ cod_emp: cod, razao: `${String(cod).padStart(2, "0")} - ${nome}` });
                setDentroDoRaio(true);
                setUsarSelectLojas(false);
            } else {
                setDentroDoRaio(false);
                Swal.fire("Atenção", "Você não está dentro do raio de uma loja.", "warning");
            }
        };

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords;
                    const resp = await api.get("lojas/localizacao");
                    const lojas = resp.data || [];

                    const lojaProxima = lojas.find((loja) => {
                        const distKm = calcularDistancia(
                            latitude,
                            longitude,
                            parseFloat(loja.latitude),
                            parseFloat(loja.longitude)
                        );
                        const distM = distKm * 1000;
                        return distM <= parseFloat(loja.distancia || 0);
                    });

                    if (lojaProxima) {
                        const { cod, nome } = extrairInfoLoja(lojaProxima);
                        setLojaSelecionada({ cod_emp: cod, razao: `${String(cod).padStart(2, "0")} - ${nome}` });
                        setDentroDoRaio(true);
                        setUsarSelectLojas(false);
                    } else {
                        await aplicarFallbackLoja(lojas);
                    }
                } catch (err) {
                    console.error(err);
                    await aplicarFallbackLoja([]);
                }
            },
            async (err) => {
                console.error(err);
                try {
                    const resp = await api.get("lojas/localizacao");
                    await aplicarFallbackLoja(resp.data || []);
                } catch (e) {
                    await aplicarFallbackLoja([]);
                }
            }
        );
    }, [chapa, navigate]);

    /* ----------------- Buscar Auditoria ----------------- */
    /* ----------------- Buscar Auditoria ----------------- */
    const buscarProdutoAuditoria = async (pesquisaParam = null) => {
        const termo = pesquisaParam ?? pesquisa;
        if (!termo || !lojaSelecionada) return;

        try {
            setLoading(true);
            setLoadingMessage("Buscando dados...");
            setProduto(null);

            const loja = lojaSelecionada.cod_emp;
            const url = `AUDIT/pesquisa?loja=${loja}&pesquisa=${encodeURIComponent(termo)}&usuario=${usuario}`;

            const resp = await api.get(url);
            const data = Array.isArray(resp.data) ? resp.data : [];

            if (!data || data.length === 0) {
                Swal.fire("Atenção", "Nenhum produto encontrado.", "warning");
                return;
            }

            // Remover duplicados pelo código do produto (seqproduto)
            const produtosUnicos = data.filter((item, index, self) =>
                index === self.findIndex((p) => p.seqproduto === item.seqproduto)
            );

            if (produtosUnicos.length > 1) {
                setListaProdutosBusca(produtosUnicos);
                setShowListaModal(true);
            } else {
                selecionarProduto(produtosUnicos[0], termo);
            }
        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Falha ao buscar produto.", "error");
        } finally {
            setLoading(false);
        }
    };

    const selecionarProduto = (item, termoOriginal = "") => {
        setProduto(item);

        const eanEncontrado =
            item.ean_principal ||
            item.codacesso ||
            item.ean ||
            (termoOriginal && termoOriginal.length > 6 ? termoOriginal : "");

        setEan(eanEncontrado);
        setShowListaModal(false);
        setPesquisa("");
    };

    /* ----------------- Estoque Outras Lojas ----------------- */
    const consultarEstoque = async () => {
        if (!produto || !lojaSelecionada) return;

        try {
            setLoading(true);
            setLoadingMessage("Consultando estoque...");

            const loja = lojaSelecionada.cod_emp;
            const url = `AUDIT/estoque?loja=${loja}&pesquisa=${produto.seqproduto}&usuario=${usuario}`;

            const resp = await api.get(url);
            const data = Array.isArray(resp.data) ? resp.data : [];

            setDadosEstoque(data);
            setShowEstoqueModal(true);
        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Erro ao consultar estoque.", "error");
        } finally {
            setLoading(false);
        }
    };

    const getImagemSrc = () => {
        const eanBusca = ean || produto?.ean_principal || produto?.codacesso || produto?.ean;
        return eanBusca ? `https://cdn-cosmos.bluesoft.com.br/products/${eanBusca}` : "";
    };

    const calcPrecoEmbalagem = (vlr) => {
        const numVal = parseCurrencyToNumber(vlr);
        const mult = parseCurrencyToNumber(produto?.multeqpemb);
        if (!mult || mult === 0 || numVal === 0) return null;
        return numVal / mult;
    };

    // Converter Imagem da Web para Base64 para exibir no componente Foto
    // Converter Imagem para Base64 usando Canvas (evita bloqueios de CORS e fetch)
    // Função para abrir o componente Foto diretamente com a URL da CDN
    const handleAmpliarFoto = () => {
        const urlImg = getImagemSrc();
        if (!urlImg) {
            Swal.fire("Atenção", "Imagem não disponível para este produto.", "warning");
            return;
        }

        setFotoSelecionada({
            foto: urlImg, // Passa a URL direta da imagem (ex: https://cdn-cosmos.bluesoft.com.br/products/EAN)
            nome: produto.desccompleta,
            info01: `Cód: ${produto.seqproduto}`,
            info02: `EAN: ${ean || produto.ean_principal || produto.codacesso || "N/A"}`
        });
    };

    return (
        <div className="audit-root-container">
            <Menu modulo="ARBOR" />

            <main className="audit-content">
                <div className="audit-card">
                    <h5 className="fw-bold text-dark text-center mb-3">🔎 Auditoria de Produto</h5>

                    {/* Campo Loja Localizada */}
                    <div className="mb-2">
                        <label className="fw-semibold extra-small text-secondary mb-1">Loja Localizada</label>
                        {usarSelectLojas ? (
                            <select
                                className="form-select form-select-sm"
                                value={lojaSelecionada?.cod_emp || ""}
                                onChange={(e) => {
                                    const codSelecionado = parseInt(e.target.value, 10);
                                    const selec = lojasPermitidasList.find((item) => {
                                        const { cod } = extrairInfoLoja(item);
                                        return cod === codSelecionado;
                                    });
                                    if (selec) {
                                        const { cod, nome } = extrairInfoLoja(selec);
                                        setLojaSelecionada({ cod_emp: cod, razao: `${String(cod).padStart(2, "0")} - ${nome}` });
                                    }
                                }}
                            >
                                {lojasPermitidasList.map((item, idx) => {
                                    const { cod, nome } = extrairInfoLoja(item);
                                    return (
                                        <option key={idx} value={cod}>
                                            {String(cod).padStart(2, "0")} - {nome}
                                        </option>
                                    );
                                })}
                            </select>
                        ) : (
                            <input
                                className="form-control form-control-sm bg-light"
                                value={
                                    lojaSelecionada?.razao ||
                                    (dentroDoRaio ? "Carregando..." : "Não localizada")
                                }
                                readOnly
                            />
                        )}
                    </div>

                    {/* Pesquisa por EAN, Código ou Descrição */}
                    <div className="mb-3">
                        <label className="fw-semibold extra-small text-secondary mb-1">EAN, Código ou Descrição</label>
                        <div className="input-group input-group-sm">
                            <input
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") buscarProdutoAuditoria();
                                }}
                                type="text"
                                maxLength={80}
                                placeholder="EAN, código ou nome (ex: arroz%palm%tp)"
                                disabled={!dentroDoRaio || loading}
                                className="form-control"
                            />
                            <button
                                className="btn btn-primary"
                                disabled={!dentroDoRaio || !pesquisa || loading}
                                onClick={() => buscarProdutoAuditoria()}
                            >
                                <i className="fas fa-search" />
                            </button>
                            <button
                                className="btn btn-secondary"
                                disabled={!dentroDoRaio || loading}
                                onClick={() => {
                                    desbloquearAudio();
                                    setShowScanner(true);
                                }}
                            >
                                <i className="fas fa-camera" />
                            </button>
                        </div>
                    </div>

                    {/* CONTEÚDO DO PRODUTO OU PLACEHOLDER */}
                    {produto ? (
                        <div className="audit-product-wrapper">
                            {/* Bloco Detalhes e Foto */}
                            <div className="border rounded-3 p-2 bg-white">
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="img-box-compact border rounded flex-shrink-0 position-relative cursor-pointer"
                                        onClick={handleAmpliarFoto}
                                        title="Clique para ampliar"
                                        style={{ cursor: "pointer" }}
                                    >
                                        <img
                                            src={getImagemSrc()}
                                            alt={produto.desccompleta}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://multilit.com.br/wp-content/uploads/2020/03/Produto-sem-foto.png";
                                            }}
                                        />
                                        <span className="position-absolute bottom-0 end-0 bg-dark text-white p-1 rounded-circle extra-small opacity-75" style={{ fontSize: "0.55rem" }}>
                                            <i className="fas fa-search-plus"></i>
                                        </span>
                                    </div>

                                    <div className="flex-grow-1 min-w-0">
                                        <h6 className="fw-bold text-dark mb-0 text-truncate">{produto.desccompleta}</h6>
                                        <div className="text-muted extra-small">
                                            <strong>Cód:</strong> {produto.seqproduto} &nbsp;|&nbsp; <strong>EAN:</strong> {ean || produto.ean_principal || produto.codacesso || "N/A"}
                                        </div>
                                        <div className="text-muted extra-small text-truncate">
                                            <strong>Família:</strong> {produto.familia ? `${produto.familia} (${produto.seqfamilia})` : "-"}
                                        </div>
                                    </div>
                                </div>

                                {/* Badges */}
                                <div className="d-flex flex-wrap gap-1 mt-2 pt-2 border-top">
                                    <span className="badge bg-light text-dark border extra-small">
                                        <i className="fas fa-boxes me-1" />
                                        {(produto.embalagem || "").substring(0, 2)}
                                    </span>
                                    <span className="badge bg-light text-dark border extra-small">
                                        {flagIcon(produto.pesavel)} Pesável
                                    </span>
                                    <span className="badge bg-light text-dark border extra-small">
                                        {flagIcon(produto.pmtmultiplicacao)} Multiplica
                                    </span>
                                    <span className="badge bg-light text-dark border extra-small">
                                        {flagIcon(produto.statuscompra)} Compra
                                    </span>
                                    <span className="badge bg-light text-dark border extra-small">
                                        {flagIcon(produto.statusvenda)} Venda
                                    </span>
                                </div>

                                <div className="extra-small text-muted mt-1">
                                    <strong>Comprador:</strong> {produto.nomecomprador || "-"}
                                </div>

                                {/* Datas */}
                                <div className="row g-1 mt-2 pt-2 border-top text-center extra-small">
                                    <div className="col-3">
                                        <span className="text-muted d-block">Tp Emb</span>
                                        <span className="fw-bold text-dark">
                                            {produto.multeqpembalagem ?? produto.multeqpemb ?? "-"}
                                        </span>
                                    </div>
                                    <div className="col-3">
                                        <span className="text-muted d-block">Últ. Ent.</span>
                                        <span className="fw-bold text-dark">
                                            {formatarData(produto.ultima_entrada) || "-"}
                                        </span>
                                    </div>
                                    <div className="col-3">
                                        <span className="text-muted d-block">Últ. Saí.</span>
                                        <span className="fw-bold text-dark">
                                            {formatarData(produto.ultima_saida) || "-"}
                                        </span>
                                    </div>
                                    <div className="col-3">
                                        <span className="text-muted d-block">Últ. Mov.</span>
                                        <span className="fw-bold text-dark">
                                            {formatarData(produto.ultima_movimentacao) || "-"}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Bloco Preços & Validações + Promoções */}
                            <div className="border rounded-3 p-2 bg-white mt-2">
                                <h6 className="fw-bold text-dark small mb-2">📈 Preços & Validações</h6>

                                <div className="d-flex justify-content-between extra-small mb-1">
                                    <span className="text-muted">Data Preço:</span>
                                    <span className="fw-bold text-dark">{formatarData(produto.dtageracaopreco)}</span>
                                </div>

                                <div className="d-flex justify-content-between extra-small mb-1">
                                    <span className="text-muted">Custo:</span>
                                    <span className="fw-bold text-dark">{formatarMoeda(produto.custo)}</span>
                                </div>

                                {/* Preço Normal */}
                                <div className="d-flex justify-content-between extra-small mb-1">
                                    <span className="text-muted">Preço Base:</span>
                                    <span className="fw-bold text-primary">
                                        {formatarMoeda(produto.precovalidnormal || produto.precobasenormal)}
                                    </span>
                                </div>

                                {/* Preço por MULTEQPEMBALAGEM (Normal) */}
                                {calcPrecoEmbalagem(produto.precovalidnormal || produto.precobasenormal) && (
                                    <div className="d-flex justify-content-between extra-small mb-1 text-secondary">
                                        <span>Preço por {produto.multeqpembalagem || "EMB"}:</span>
                                        <span className="fw-semibold">
                                            {formatarMoeda(calcPrecoEmbalagem(produto.precovalidnormal || produto.precobasenormal))}
                                        </span>
                                    </div>
                                )}

                                {/* PROMOVAREJO */}
                                {parseCurrencyToNumber(produto.promovarejo) > 0 && (
                                    <div className="p-2 bg-danger-subtle rounded mt-2 border border-danger">
                                        <div className="d-flex justify-content-between extra-small fw-bold text-danger">
                                            <span>🏷️ Promoção Varejo:</span>
                                            <span>{formatarMoeda(produto.promovarejo)}</span>
                                        </div>

                                        {calcPrecoEmbalagem(produto.promovarejo) && (
                                            <div className="d-flex justify-content-between extra-small text-danger opacity-75 mt-1">
                                                <span>Preço por {produto.multeqpembalagem || "EMB"}:</span>
                                                <span>{formatarMoeda(calcPrecoEmbalagem(produto.promovarejo))}</span>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between extra-small text-muted mt-1 pt-1 border-top border-danger-subtle">
                                            <span>Vigência:</span>
                                            <span>{formatarData(produto.iniciovarejo)} até {formatarData(produto.fimvarejo)}</span>
                                        </div>
                                    </div>
                                )}

                                {/* PROMOAPP */}
                                {parseCurrencyToNumber(produto.promoapp) > 0 && (
                                    <div className="p-2 bg-success-subtle rounded mt-2 border border-success">
                                        <div className="d-flex justify-content-between extra-small fw-bold text-success">
                                            <span>📱 Promoção App:</span>
                                            <span>{formatarMoeda(produto.promoapp)}</span>
                                        </div>

                                        {calcPrecoEmbalagem(produto.promoapp) && (
                                            <div className="d-flex justify-content-between extra-small text-success opacity-75 mt-1">
                                                <span>Preço por {produto.multeqpembalagem || "EMB"}:</span>
                                                <span>{formatarMoeda(calcPrecoEmbalagem(produto.promoapp))}</span>
                                            </div>
                                        )}

                                        <div className="d-flex justify-content-between extra-small text-muted mt-1 pt-1 border-top border-success-subtle">
                                            <span>Vigência App:</span>
                                            <span>{formatarData(produto.inicioapp)} até {formatarData(produto.fimapp)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Bloco Estoques */}
                            <div className="border rounded-3 p-2 bg-white mt-2">
                                <h6 className="fw-bold text-dark small mb-2">📦 Estoques</h6>

                                <div className="d-flex justify-content-between extra-small mb-1">
                                    <span className="text-muted">Estoque Loja:</span>
                                    <span className="fw-bold text-dark">
                                        {produto.estqloja ?? 0} {(produto.embalagem || "").substring(0, 2)}
                                    </span>
                                </div>

                                <div className="d-flex justify-content-between extra-small mb-1">
                                    <span className="text-muted">Estoque CD:</span>
                                    <span className="fw-bold text-dark">{produto.estqcd ?? 0}</span>
                                </div>

                                <div className="d-flex justify-content-between extra-small mb-1">
                                    <span className="text-muted">Estoque Depósito:</span>
                                    <span className="fw-bold text-dark">{produto.estqdeposito ?? 0}</span>
                                </div>

                                <div className="d-flex justify-content-between extra-small mb-2">
                                    <span className="text-muted">Mín / Máx:</span>
                                    <span className="fw-bold text-dark">
                                        {produto.estqmin ?? produto.min ?? "-"} / {produto.estqmax ?? produto.max ?? "-"}
                                    </span>
                                </div>

                                <Button
                                    variant="outline-primary"
                                    className="w-100 btn-sm py-1 fw-bold extra-small"
                                    onClick={consultarEstoque}
                                >
                                    <i className="fas fa-list me-1" />
                                    Ver Estoques Outras Lojas
                                </Button>
                            </div>
                        </div>
                    ) : (
                        /* Estado Inicial Compacto */
                        <div className="audit-empty-state">
                            <i className="fas fa-search fa-2x text-secondary opacity-25 mb-2"></i>
                            <div className="text-muted extra-small">Pesquise por EAN, código interno ou nome do produto</div>
                        </div>
                    )}
                </div>
            </main>

            {/* Modal de Lista de Produtos Encontrados */}
            <ModalListaProdutos
                show={showListaModal}
                produtos={listaProdutosBusca}
                onSelect={(item) => selecionarProduto(item)}
                onClose={() => setShowListaModal(false)}
            />

            {/* Modal de Estoque Outras Lojas */}
            <Modal show={showEstoqueModal} onHide={() => setShowEstoqueModal(false)} centered size="md">
                <Modal.Header closeButton className="py-2">
                    <Modal.Title className="fs-6 fw-bold">
                        📦 Estoque por Loja <div>
                            <span className="text-primary">{produto?.desccompleta} ({produto?.seqproduto})</span>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-2">
                    {dadosEstoque?.length > 0 ? (
                        <div style={{ maxHeight: "55vh", overflowY: "auto" }}>
                            <EstoqueListaCards dados={dadosEstoque} />
                        </div>
                    ) : (
                        <div className="alert alert-warning text-center m-0 p-2 extra-small">
                            Nenhum dado de estoque encontrado para outras lojas.
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-1">
                    <Button variant="secondary" size="sm" onClick={() => setShowEstoqueModal(false)}>
                        Fechar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Scanner Modal */}
            <BarcodeModal
                show={showScanner}
                onClose={() => setShowScanner(false)}
                onScanSuccess={(codigo) => {
                    if (isProcessingScan) return;

                    setIsProcessingScan(true);
                    playBeep();
                    setShowScanner(false);
                    setPesquisa(codigo);

                    buscarProdutoAuditoria(codigo).finally(() =>
                        setTimeout(() => setIsProcessingScan(false), 900)
                    );
                }}
            />

            {/* Componente de Ampliar Foto Integrado */}

            <Foto
                show={!!fotoSelecionada}
                onClose={() => setFotoSelecionada(null)}
                foto={fotoSelecionada?.foto}
                nome={fotoSelecionada?.nome}
                info01={fotoSelecionada?.info01}
                info02={fotoSelecionada?.info02}
            />

            <LoadingModal show={loading} message={loadingMessage} />

            <Footer />
        </div>
    );
}